import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { RiCloseLine, RiDeleteBin6Line } from "react-icons/ri";
import axios from "axios";

const HospitalModal = ({ hospital, isEditing, onClose, onSave }) => {
  // Separate state variables
  const [name, setName] = useState(hospital?.name || "");
  const [address, setAddress] = useState(hospital?.address || "");
  const [phoneNumber, setPhoneNumber] = useState(hospital?.phoneNumber || "");
  const [hospitalSpecialization, setHospitalSpecialization] = useState(
    hospital?.hospitalSpecialization?.join(", ") || ""
  );
  const [mapIframe, setMapIframe] = useState(hospital?.mapIframe || "");
  const [hospitalImage, setHospitalImage] = useState(null);
  const [doctors, setDoctors] = useState(hospital?.doctors || []);

  const [imagePreview, setImagePreview] = useState(
    hospital?.hospitalImage || null
  );
  const [doctorName, setDoctorName] = useState(""); // Input field for adding doctors
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hospital) {
      setName(hospital.name || "");
      setAddress(hospital.address || "");
      setPhoneNumber(hospital.phoneNumber || "");
      setHospitalSpecialization(
        hospital.hospitalSpecialization?.join(", ") || ""
      );
      setMapIframe(hospital.mapIframe || "");
      setHospitalImage(null);
      setDoctors(hospital.doctors || []);
      setImagePreview(hospital.hospitalImage || null);
    }
  }, [hospital]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHospitalImage(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddDoctor = () => {
    if (doctorName.trim() === "") return;
    setDoctors([...doctors, { name: doctorName.trim(), specialization: "" }]);
    setDoctorName(""); // Clear input
  };

  const handleRemoveDoctor = async (index, id) => {
    try {
      await axios.delete(`https://api.swasthmitra.com/api/doctor/deletedoctor/${id}`);
      setDoctors(doctors.filter((_, i) => i !== index));
    } catch (error) {
      console.error("Error removing doctor:", error);
      setError("Error removing doctor");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // console.log("data",name, hospitalSpecialization, address, phoneNumber, mapIframe);
      const formDataToSend = new FormData();
      formDataToSend.append("name", name.trim());
      formDataToSend.append("hospitalSpecialization", hospitalSpecialization);
      formDataToSend.append("address", address.trim());
      formDataToSend.append("phoneNumber", phoneNumber.trim());
      formDataToSend.append("mapIframe", mapIframe.trim());

      if (hospitalImage) {
        formDataToSend.append("image", hospitalImage);
      }

      console.log("FormData:", Object.fromEntries(formDataToSend.entries()));

      // const url = isEditing
      //   ? `https://api.swasthmitra.com/api/hospital/updatehospital/${hospital._id}`
      //   : "https://api.swasthmitra.com/api/hospital/createhospital";

      // const response = await axios({
      //   method: isEditing ? "put" : "post",
      //   url: url,
      //  {
      //   name: name.trim(),
      //  },
      //   headers: { "Content-Type": "multipart/form-data" },
      // });

      // if (response.data.success) {
      //   onSave();
      // } else {
      //   throw new Error(response.data.message);
      const response = await axios.put(
        `https://api.swasthmitra.com/api/hospital/updatehospital/${hospital._id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log(response);
      if (response.data.success) {
        onSave();
      } else {
        throw new Error(response.data.message);
      }

      // }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Error saving hospital"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.5, opacity: 0 }}
      >
        <button className="close-btn" onClick={onClose}>
          <RiCloseLine />
        </button>

        <div className="modal-header">
          <h2>{isEditing ? "Edit Hospital" : "Add Hospital"}</h2>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Hospital Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Specializations (comma-separated)</label>
            <input
              type="text"
              value={hospitalSpecialization}
              onChange={(e) => setHospitalSpecialization(e.target.value)}
              required
              placeholder="e.g., Cardiology, Neurology, Orthopedics"
            />
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Hospital Image</label>
            <input type="file" onChange={handleImageChange} accept="image/*" />
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Hospital preview" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Google Maps Embed Code</label>
            <textarea
              value={mapIframe}
              onChange={(e) => setMapIframe(e.target.value)}
              placeholder="Paste Google Maps iframe code here"
              rows="4"
            />
            {mapIframe && (
              <div
                className="map-preview"
                dangerouslySetInnerHTML={{ __html: mapIframe }}
              />
            )}
          </div>

          {/* Doctors Section */}
          <div className="form-group">
            <label>Doctors</label>
            <table className="doctor-table">
              <thead>
                <tr>
                  <th>S.N</th>
                  <th>Doctor Name</th>
                  <th>Specialization</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {doctors.map((doctor, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{doctor?.name}</td>
                    <td>{doctor?.specialization}</td>
                    <td>
                      <RiDeleteBin6Line
                        cursor="pointer"
                        color="red"
                        onClick={() => handleRemoveDoctor(index, doctor._id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Hospital"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default HospitalModal;
